import { useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface MajorComboboxProps {
  value?: string;
  onChange: (value: string) => void;
  majors: string[];
  placeholder?: string;
  exclude?: string[];
  className?: string;
}

/** Shared searchable major picker used in onboarding and school discovery. */
export default function MajorCombobox({
  value = "",
  onChange,
  majors,
  placeholder = "Search majors…",
  exclude = [],
  className,
}: MajorComboboxProps) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const visibleMajors = majors.filter(major => !exclude.includes(major));

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          onKeyDown={event => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setOpen(true);
            }
          }}
          className={cn(
            "flex h-11 w-full items-center justify-between rounded-xl border border-input bg-background px-3 text-left text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            className
          )}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onOpenAutoFocus={event => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <Command>
          <CommandInput ref={inputRef} placeholder="Type to search majors…" />
          <CommandList>
            <CommandEmpty>No majors found.</CommandEmpty>
            <CommandGroup>
              {visibleMajors.map(major => (
                <CommandItem
                  key={major}
                  value={major}
                  onSelect={() => {
                    onChange(value === major ? "" : major);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4", value === major ? "opacity-100" : "opacity-0")}
                  />
                  {major}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
